/*
 * VerifyBadge — the single source of truth for a trust verdict.
 *
 * Colour == meaning, always:
 *   verified  → teal   (proven from live data)
 *   attested  → gold   (attested, not independently re-verified here)
 *   pending   → ember  (in flight / queued / store-and-forward not yet acked)
 *   failed    → red    (verification ran and FAILED — LOUD)
 *   offline   → red    (plane offline, cannot substantiate — LOUD)
 *   unknown   → grey   (no evidence — never dressed up as success)
 *
 * UX LAW: the badge NEVER claims success it cannot back. Pass `onInspect` to
 * make it expand its proof (the caller wires a ProvabilityDrawer).
 */

import type { VerifyState } from "../api/types";

interface VerifyBadgeProps {
  state: VerifyState;
  /** Override the default label text. */
  label?: string;
  /** Click handler — typically opens the ProvabilityDrawer with the evidence. */
  onInspect?: () => void;
  /** Compact pill (icon + short word) vs full. */
  size?: "sm" | "md";
}

const META: Record<
  VerifyState,
  { word: string; glyph: string; accent: string; loud: boolean }
> = {
  verified: { word: "Verified", glyph: "✓", accent: "var(--teal)", loud: false },
  attested: { word: "Attested", glyph: "◆", accent: "var(--gold)", loud: false },
  pending: { word: "Pending", glyph: "◴", accent: "var(--ember)", loud: false },
  failed: { word: "Failed", glyph: "✕", accent: "var(--red)", loud: true },
  offline: { word: "Offline", glyph: "⦸", accent: "var(--red)", loud: true },
  unknown: { word: "Unsubstantiated", glyph: "?", accent: "var(--fg-faint)", loud: false },
};

export function VerifyBadge({ state, label, onInspect, size = "md" }: VerifyBadgeProps) {
  const m = META[state];
  const text = label ?? m.word;
  const interactive = typeof onInspect === "function";

  const style: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontFamily: "var(--font-mono)",
    fontSize: size === "sm" ? "11px" : "12px",
    lineHeight: 1,
    padding: size === "sm" ? "4px 8px" : "6px 10px",
    minHeight: interactive ? "32px" : undefined,
    borderRadius: "999px",
    color: m.accent,
    border: `1px solid ${m.accent}`,
    background: m.loud ? "var(--red-dim)" : "transparent",
    letterSpacing: "0.04em",
    fontWeight: m.loud ? 600 : 500,
    cursor: interactive ? "pointer" : "default",
  };

  const content = (
    <>
      <span aria-hidden="true">{m.glyph}</span>
      <span>{text}</span>
      {interactive && (
        <span aria-hidden="true" style={{ opacity: 0.6 }}>
          ↗
        </span>
      )}
    </>
  );

  if (interactive) {
    return (
      <button
        type="button"
        style={style}
        onClick={onInspect}
        aria-label={`${text} — inspect proof`}
      >
        {content}
      </button>
    );
  }

  return (
    <span style={style} role="status" aria-label={text}>
      {content}
    </span>
  );
}
